import { formatCount } from "utils/chartsPalette";

// Every label, subtitle and derived figure the variants display, in one place.
//
// The rule for this whole exercise is that A, B and C show EXACTLY what V1
// shows — only layout, styling and motion change. Three components each
// deriving "invitations sent in this range" from the payload would drift the
// moment one of them was edited, so they all read it from here. If a figure is
// wrong it is wrong in all four views and gets fixed once.
//
// Strings are copied verbatim from the shipped components, not paraphrased.

export const metrics = (data, monetization) => [
  {
    key: "new_startups",
    label: "New Startups",
    value: data?.kpis?.new_startups?.value,
    delta: data?.kpis?.new_startups?.delta_pct,
    higherIsBetter: true,
    footnote: data?.totals?.startups != null ? `${formatCount(data.totals.startups)} all-time` : "",
    series: "startups",
    // Stroke paths, so each tile's icon matches the shipped KPI strip's intent.
    icon: "M5 20V9l7-5 7 5v11M10 20v-5h4v5",
  },
  {
    key: "new_investors",
    label: "New Investors",
    value: data?.kpis?.new_investors?.value,
    delta: data?.kpis?.new_investors?.delta_pct,
    higherIsBetter: true,
    footnote: data?.totals?.investors != null ? `${formatCount(data.totals.investors)} all-time` : "",
    series: "investors",
    icon: "M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6",
  },
  {
    key: "invitations_today",
    label: "Round Invitations Today",
    value: data?.kpis?.invitations_today?.value,
    // No delta: "today" is deliberately not a range comparison.
    delta: null,
    higherIsBetter: true,
    footnote: data?.totals?.invitations != null ? `${formatCount(data.totals.invitations)} all-time` : "",
    series: "invitations",
    icon: "M3 11.5 21 3l-8.5 18-2.5-7.5z",
  },
  {
    key: "subscriptions",
    label: "Subscriptions Purchased",
    value: monetization?.subscriptions?.value,
    delta: monetization?.subscriptions?.delta_pct,
    higherIsBetter: true,
    footnote:
      monetization?.totals?.subscriptions != null
        ? `${formatCount(monetization.totals.subscriptions)} all-time`
        : "",
    // No sparkline: subscriptions have no daily series in the payload.
    series: null,
    icon: "M3 7h18v12H3zM3 11h18M7 15h4",
  },
];

export const pipeline = (data) => {
  const p = data?.pipeline ?? {};
  const total = p.total ?? 0;
  const matched = (p.interested ?? 0) + (p.invested ?? 0);
  const rate = (part, whole) => (whole ? `${Math.round((part / whole) * 1000) / 10}%` : "—");

  return {
    total,
    matched,
    subtitle:
      total === 0
        ? "No invitations in this range"
        : `${formatCount(total)} invitations sent in this range · where they stand now`,
    stages: [
      { key: "invited", label: "Invited, No Response", n: p.invited ?? 0 },
      { key: "interested", label: "Interested", n: p.interested ?? 0 },
      { key: "invested", label: "Invested", n: p.invested ?? 0 },
      { key: "rejected", label: "Rejected", n: p.rejected ?? 0 },
    ],
    rates: [
      { label: "Invited → Interested", value: rate(p.interested ?? 0, total) },
      { label: "Interested → Invested", value: rate(p.invested ?? 0, matched) },
      {
        label: "Match Rate",
        value: data?.kpis?.match_rate?.value == null ? "—" : `${data.kpis.match_rate.value}%`,
      },
    ],
  };
};

export const activity = {
  subtitle: "Each panel is scaled to its own series, so a spike in one cannot flatten the other",
  panels: [
    { key: "startups", name: "New Startups" },
    { key: "invitations", name: "Invitations" },
  ],
};

export const planMix = (monetization) => {
  const u = monetization?.users ?? {};
  const rate = monetization?.subscription_rate?.value;
  return {
    subtitle: `${monetization?.by_plan?.total ?? 0} startups bought a plan in this range`,
    legend: [
      { key: "paid", label: "Paid", value: u.paid ?? 0 },
      { key: "paid_earlier", label: "Paid Earlier", value: u.paid_earlier ?? 0 },
      { key: "free", label: "Free / Lite", value: u.free ?? 0 },
    ],
    total: u.total ?? 0,
    // "to date": the split bar's population is every startup ever, which is
    // the one thing on this page the range cannot bound — see MonetizationBand.
    footnote:
      rate === null || rate === undefined
        ? `of ${u.total ?? 0} startups to date`
        : `of ${u.total ?? 0} startups to date · ${rate}% of those who took an assessment went on to pay`,
    plans: monetization?.by_plan?.rows ?? [],
  };
};

export const assessments = (monetization) => ({
  subtitle: `${monetization?.assessments?.total ?? 0} in this range`,
  legend: [
    { key: "paid", label: "Paid" },
    { key: "free", label: "Free" },
  ],
  rows: (monetization?.assessments?.rows ?? []).map((r) => ({
    ...r,
    footnote: `${r.paid} paid · ${r.free} free`,
  })),
});

export const composition = (data) => ({
  summary: `${data?.by_stage?.total ?? 0} startups · a startup may hold more than one`,
  groups: [
    {
      key: "country",
      title: "Top Countries",
      rows: data?.by_country?.rows ?? [],
      footer: `${data?.by_country?.total ?? 0} startups`,
      expandable: false,
    },
    {
      key: "stage",
      title: "Stage",
      rows: data?.by_stage?.rows ?? [],
      footer: `${data?.by_stage?.total ?? 0} startups · may hold more than one`,
      expandable: false,
    },
    {
      key: "category",
      title: "Category & Sector",
      rows: data?.by_category?.rows ?? [],
      footer: `${data?.by_category?.total ?? 0} startups · may hold more than one · select a category for sectors`,
      expandable: true,
    },
  ],
});

export const campaigns = (data) => {
  const rows = data?.campaigns?.rows ?? [];
  return {
    summary: `${rows.length} ${rows.length === 1 ? "source" : "sources"} · signups attributed to a campaign`,
    description:
      "Signups by acquisition campaign, and what those people became. A signup is one person; a startup or investor profile is what they went on to create, so the two can exceed the signups where one person made both.",
    // Biggest campaign first, as the shipped table orders them.
    rows: [...rows].sort((a, b) => (b?.signups ?? 0) - (a?.signups ?? 0)),
  };
};

export const seriesFrom = (daily) => ({
  startups: (daily ?? []).map((d) => d.startups ?? 0),
  invitations: (daily ?? []).map((d) => d.invitations ?? 0),
  investors: (daily ?? []).map((d) => d.investors ?? 0),
});
